import { ChangeDetectionStrategy, Component, computed, inject, input, output, viewChild, ViewEncapsulation } from '@angular/core';
import { DetailPaneComponent, DetailPaneContext } from '@workbuddy/ui/v2/blocks/detail.pane';
import { EntityBoardStore } from '../../state';
import { BoardPanelComponent } from '../board.panel/board.panel.component';
import { BOARD_PANEL_NO_EXPAND, BoardPanelExpand } from '../board.panel/board.panel.expand';
import type { BoardPanelRefreshScope, BoardPanelRowPatch } from '../board.panel/board.panel.host';

/**
 * The quickview panel in its dialog presentation. Mounted by the board through WorkBuddyDialogService
 * with the board's injector, so the store, panel layout and focused row are the same ones the side
 * panel reads — only the frame differs.
 *
 * Outputs mirror `board-panel` one for one: the board handles a dialog panel exactly like a side one.
 */
@Component({
  selector: 'board-panel-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [BoardPanelComponent, DetailPaneComponent],
  templateUrl: './board.panel.dialog.component.html',
  styleUrls: ['./board.panel.dialog.component.less'],
})
export class BoardPanelDialogComponent {

  protected readonly store = inject(EntityBoardStore);
  private readonly $pane = viewChild(DetailPaneComponent);

  public $context = input<DetailPaneContext | null>(null, { alias: 'context' });
  public $title = input('', { alias: 'title' });
  /** The record's entity type, shown on hovering the title. */
  public $titleTooltip = input('', { alias: 'titleTooltip' });
  public $ownsHeader = input(true, { alias: 'ownsHeader' });
  public $expand = input<BoardPanelExpand>(BOARD_PANEL_NO_EXPAND, { alias: 'expand' });
  public $recordPageUrl = input<string | null>(null, { alias: 'recordPageUrl' });

  public closed = output<void>();
  public stepRequested = output<1 | -1>();
  public refreshRequested = output<BoardPanelRefreshScope>();
  public rowPatchRequested = output<BoardPanelRowPatch>();
  public recordUpserted = output<unknown>();
  public expandRequested = output<void>();
  public dockRequested = output<void>();

  /** Read by the board before a focus change: an upload in the pane must pass the leave guard first. */
  public readonly $busy = computed(() => this.$pane()?.$busy() === true);

  /** The same leave guard the pane runs for its own child navigation: patch surfaces flush, drafts prompt. */
  public canLeave(): Promise<boolean> {

    return this.$pane()?.canDeactivate() ?? Promise.resolve(true);
  }
}
